<template>
  <div class="stats">
    <!-- FILTRES DES STATS -->
    <v-card class="mb-3" v-if="$route.fullPath && $route.fullPath.indexOf('reports') == -1">
      <v-card-text>
        <v-layout row wrap>
          <!-- PÉRIODE -->
          <v-flex xs12 md4>
            <v-layout row wrap>
              <v-flex xs12>
                <v-select 
                  dense
                  :items="periods" 
                  v-model="period"
                  :label="$t('period') | cap"
                  prepend-icon="date_range"
                  :disabled="periodDisabled"
                ></v-select>
              </v-flex>
              <!-- DATE DE DÉBUT -->
              <v-flex xs12 sm6>
                <v-dialog
                  v-model="modalStart"
                  lazy
                  full-width
                  width="290px"
                  :disabled="period != 0"
                >
                  <v-text-field
                    slot="activator"
                    :label="$t('startDate') | cap"
                    v-model="dateStartDisplayed"
                    @blur="dateStartPicker = parseDate(dateStartDisplayed)"
                    prepend-icon="event"
                    readonly
                    :disabled="period != 0"
                  ></v-text-field>
                  <v-date-picker
                    v-model="dateStartPicker"
                    @input="dateStartDisplayed = formatDate($event); modalStart = false"
                    :first-day-of-week="1"
                    :min="allowedDatesStart.min"
                    :max="allowedDatesStart.max"
                    :locale="$i18n.locale"
                    scrollable
                    color="primary"
                  >
                  </v-date-picker>
                </v-dialog>
              </v-flex>
              <!-- DATE DE FIN -->
              <v-flex xs12 sm6>
                <v-dialog
                  v-model="modalEnd"
                  lazy
                  full-width
                  width="290px"
                  :disabled="period != 0"
                >
                  <v-text-field
                    slot="activator"
                    :label="$t('endDate') | cap"
                    v-model="dateEndDisplayed"
                    @blur="dateEndPicker = parseDate(dateEndDisplayed)"
                    prepend-icon="event"
                    readonly
                    :disabled="period != 0"
                  ></v-text-field>
                  <v-date-picker
                    v-model="dateEndPicker"
                    @input="dateEndDisplayed = formatDate($event); modalEnd = false"
                    :first-day-of-week="1"
                    :min="allowedDatesEnd.min"
                    :max="allowedDatesEnd.max"
                    :locale="$i18n.locale"
                    scrollable
                    color="primary"
                  >
                  </v-date-picker>
                </v-dialog>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- SITE -->
          <v-flex xs6 md2 v-if="$route.name !== 'sales' || !HAS_ONE_FUNNEL">
            <v-layout row wrap>
              <v-flex xs12 pt-2 :class="{'ml-5': $vuetify.breakpoint.mdAndUp}">
                <v-btn
                  color="primary"
                  outline
                  :block="$vuetify.breakpoint.smAndDown"
                  class="ml-0"
                  @click="openDialogSites()"
                >
                  <v-icon left color="primary">language</v-icon>
                  {{ site.length }} / {{ $tc('sites', $store.state._Sites.sites.length, { count: $store.state._Sites.sites.length }) }}
                </v-btn>
                <!-- Sélectionner les sites -->
                <v-dialog v-model="dialogSites" scrollable persistent max-width="min-content">
                  <v-card>
                    <v-card-title class="primary white--text">
                      <div class="headline">{{ $t('select') + ' ' + $tc('sites', 2, {count: ''})}}</div>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text style="height: 400px;">
                      <v-data-table
                        :headers="headersSites"
                        :items="$store.state._Sites.sites"
                        v-model="siteTmp"
                        item-key="LIC_ID"
                        select-all="primary"
                        class="elevation-1"
                        hide-actions
                        :total-items="$store.state._Sites.sites.length"
                      >
                        <template slot="headerCell" slot-scope="props">
                          <span class="subheading">{{ props.header.text }}</span>
                        </template>
                        <template slot="items" slot-scope="props">
                          <td>
                            <v-checkbox
                              color="primary"
                              v-model="props.selected"
                            ></v-checkbox>
                          </td>
                          <td>{{ props.item.LIC_URL }}</td>
                        </template>
                      </v-data-table>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn flat @click.native="dialogSites = false">{{ $t('cancel') }}</v-btn>
                      <v-btn :disabled="!siteTmp.length" color="primary" @click.native="submitDialogSites()">{{ $t('submit') }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>
            </v-layout>
          </v-flex>
          <!-- MOTIFS -->
          <v-flex xs6 md2 v-if="showSelectMotifs">
            <wz-select-motifs
              :motifs.sync="motif"
              :motifsInit="motifInitialisation"
            ></wz-select-motifs>
          </v-flex>
          <!-- AGENT -->
          <v-flex xs6 md2 v-if="showSelectAgents">
            <wz-select-agents
              :agents.sync="agent"
              :agentsInit="$store.state._User.colleagues"
              :min="1"
            ></wz-select-agents>
          </v-flex>
          <!-- BOTS -->
          <v-flex xs6 md2 v-if="showSelectBots">
            <wz-select-bots
              :bots.sync="bot"
              :botsInit="$store.state._User.bots"
              :min="1"
            ></wz-select-bots>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <!-- AFFICHAGE DES RÉSULTATS -->
    <transition name="slide-x-reverse-transition" mode="out-in">
      <router-view></router-view>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash'
import * as moment from 'moment'
import { mapGetters, mapState } from 'vuex'

export default {
  data () {
    return {
      /**
       * indique si les stats ont été rafraichies avec les filtres affichés
       * @type {Boolean}
       */
      filtered: false,

      dateStartPicker: null,
      dateStartDisplayed: null,
      dateEndPicker: null,
      dateEndDisplayed: null,

      modalStart: false,
      modalEnd: false,
      periods: [],
      // periods: [
      //   { text: _.upperFirst(this.$tc('custom', 2)), value: 0 },
      //   { text: _.upperFirst(this.$t('yesterday')), value: 1 },
      //   { text: _.upperFirst(this.$t('thisWeek')), value: 2 },
      //   { text: _.upperFirst(this.$t('thisMonth')), value: 3 },
      //   { text: _.upperFirst(this.$t('last7Days')), value: 4 },
      //   { text: _.upperFirst(this.$t('last30Days')), value: 5 },
      //   { text: _.upperFirst(this.$t('lastWeek')), value: 6 },
      //   { text: _.upperFirst(this.$t('lastMonth')), value: 7 },
      //   { text: _.upperFirst(this.$t('lastQuarter')), value: 8 }
      // ],
      period: {},
      // indique si la période ne peut pas être changée (exemple: les ventes)
      periodDisabled: false,

      dialogSites: false, // indique si le dialog de sélection des sites est ouverte
      siteTmp: [], // liste des sites gérés dans la dialog des sites
      site: [], // liste des sites sélectionnés et qui font office de filtres

      agent: [], // liste des agents choisis
      bot: [], // liste des bots choisis
      motif: [] // liste des motifs choisis
    }
  },

  created () {
    this.initPeriod()
    this.initSites()
    this.initAgents()
    this.initBots()
    this.initMotifs()
    this.initChannels()
    // this.initPages()
    // période plus ou longue suivant la bride
    if (this.entLevel === '7') {
      // 1 an de données max
      this.periods = [
        { text: _.upperFirst(this.$tc('custom', 2)), value: 0 },
        { text: _.upperFirst(this.$t('yesterday')), value: 1 },
        { text: _.upperFirst(this.$t('thisWeek')), value: 2 },
        { text: _.upperFirst(this.$t('thisMonth')), value: 3 },
        { text: _.upperFirst(this.$t('last7Days')), value: 4 },
        { text: _.upperFirst(this.$t('last30Days')), value: 5 },
        { text: _.upperFirst(this.$t('lastWeek')), value: 6 },
        { text: _.upperFirst(this.$t('lastMonth')), value: 7 },
        { text: _.upperFirst(this.$t('lastQuarter')), value: 8 }
      ]
    } else if (this.entLevel === '6') {
      // 30 jours de données max
      this.periods = [
        { text: _.upperFirst(this.$tc('custom', 2)), value: 0 },
        { text: _.upperFirst(this.$t('yesterday')), value: 1 },
        { text: _.upperFirst(this.$t('thisWeek')), value: 2 },
        { text: _.upperFirst(this.$t('thisMonth')), value: 3 },
        { text: _.upperFirst(this.$t('last7Days')), value: 4 },
        { text: _.upperFirst(this.$t('last30Days')), value: 5 },
        { text: _.upperFirst(this.$t('lastWeek')), value: 6 }
      ]
    } else if (this.entLevel === '5') {
      // 1 semaine de données max
      this.periods = [
        { text: _.upperFirst(this.$tc('custom', 2)), value: 0 },
        { text: _.upperFirst(this.$t('yesterday')), value: 1 },
        { text: _.upperFirst(this.$t('thisWeek')), value: 2 },
        { text: _.upperFirst(this.$t('last7Days')), value: 4 }
      ]
    } else if (this.entLevel === '10' || this.entLevel === '1') {
      // 1 semaine de données max
      this.periods = [
        { text: _.upperFirst(this.$tc('custom', 2)), value: 0 },
        { text: _.upperFirst(this.$t('yesterday')), value: 1 }
      ]
    }
  },

  mounted () {
  },

  computed: {
    ...mapGetters('_Sites', ['HAS_ONE_MOTIF', 'HAS_ONE_FUNNEL']),
    ...mapState('_User', ['entLevel']),
    maxDatesStart: () => this.parseDate(this.dateEndPicker),
    minDatesStart: () => this.parseDate(this.formatDate(new Date(moment().subtract(1, 'year')))),
    allowedDatesStart () {
      // période plus ou longue suivant la bride
      if (this.entLevel === '7') {
        return {
          max: this.parseDate(this.dateEndPicker),
          min: this.parseDate(this.formatDate(new Date(moment().subtract(1, 'year'))))
        }
      } else if (this.entLevel === '6') {
        return {
          max: this.parseDate(this.dateEndPicker),
          min: this.parseDate(this.formatDate(new Date(moment().subtract(1, 'month'))))
        }
      } else if (this.entLevel === '5') {
        return {
          max: this.parseDate(this.dateEndPicker),
          min: this.parseDate(this.formatDate(new Date(moment().subtract(1, 'week'))))
        }
      } else if (this.entLevel === '10' || this.entLevel === '1') {
        return {
          max: this.parseDate(this.dateEndPicker),
          min: this.parseDate(this.formatDate(new Date(moment().subtract(1, 'day'))))
        }
      }
    },
    maxDatesEnd: () => this.parseDate(this.formatDate(new Date(moment().local().endOf('day')))),
    minDatesEnd: () => this.parseDate(this.dateStartPicker),
    allowedDatesEnd () {
      return {
        max: this.parseDate(this.formatDate(new Date(moment().local().endOf('day')))),
        min: this.parseDate(this.dateStartPicker)
      }
    },
    nbDays () {
      return moment(this.dateEndPicker).diff(moment(this.dateStartPicker), 'days') + 1
    },
    headersSites () {
      return !this.siteTmp.length
      ? [{text: _.upperFirst(this.$t('all')), align: 'left', sortable: false}]
      : [{text: _.upperFirst(this.$t('none')), align: 'left', sortable: false}]
    },
    showSelectAgents () {
      return ['reports', 'audience', 'chatbot', 'C2C'].indexOf(this.$route.name) === -1
    },
    showSelectBots () {
      return ['chatbot'].indexOf(this.$route.name) !== -1
    },
    showSelectMotifs () {
      // return ['chats', 'chatbot', 'agents-perf', 'satisfaction'].indexOf(this.$route.name) !== -1 && this.HAS_ONE_MOTIF
      return ['chatbot', 'agents-perf', 'satisfaction'].indexOf(this.$route.name) !== -1 && this.HAS_ONE_MOTIF
    },
    motifInitialisation () {
      return [{'MOT_ID': '', 'MOT_CODE': 'WZNOMOTIF', 'MOT_LABEL': this.$t('nomotif')}, ...this.$store.state._Sites.sites[0].motifs]
    }
  },

  methods: {
    initPeriod () {
      // periode
      this.period = _.isNil(this.$store.state._Stats.period)
      ? 1 // hier
      : this.$store.state._Stats.period
      console.log('this.entLevel', this.entLevel)
      // si c'est du first, c'est forcement 1
      if (this.entLevel === '10' || this.entLevel === '1') {
        this.period = 1
      }
      console.log('this.period', this.period)
    },
    initDates () {
      // récupère les dates qui sont dans le store si la période n'est pas personnalisée
      let periodPerso = (this.period === 0)
      console.log('periodPerso', periodPerso)
      if (periodPerso) {
        // date de fin
        // par défaut la date de fin est la date du jour
        this.dateEndPicker = _.isEmpty(this.$store.state._Stats.dateEnd)
        ? new Date(moment().startOf('day'))
        : new Date(this.$store.state._Stats.dateEnd)
        // date de début
        // par défaut la date de début est une semaine avant la date du jour
        this.dateStartPicker = _.isEmpty(this.$store.state._Stats.dateEnd)
        ? new Date(moment().subtract(1, 'week'))
        : new Date(this.$store.state._Stats.dateStart)
      } else {
        this.dateEndPicker = new Date(moment())
        switch (this.period) {
          case 1: // Hier
            this.dateStartPicker = new Date(moment().subtract(1, 'day'))
            this.dateEndPicker = new Date(moment().subtract(1, 'day'))
            break
          case 2: // Cette semaine
            this.dateStartPicker = new Date(moment().startOf('week'))
            this.dateEndPicker = new Date(moment().subtract(0, 'day'))
            break
          case 3: // Ce mois-ci
            this.dateStartPicker = new Date(moment().startOf('month'))
            this.dateEndPicker = new Date(moment().subtract(0, 'day'))
            break
          case 4: // 7 derniers jours
            this.dateStartPicker = new Date(moment().subtract(7, 'day'))
            this.dateEndPicker = new Date(moment().subtract(1, 'day'))
            break
          case 5: // 30 derniers jours
            this.dateStartPicker = new Date(moment().subtract(30, 'day'))
            this.dateEndPicker = new Date(moment().subtract(1, 'day'))
            break
          case 6: // la semaine dernière
            this.dateStartPicker = new Date(moment().startOf('week').subtract(1, 'week'))
            this.dateEndPicker = new Date(moment().startOf('week').subtract(1, 'day'))
            break
          case 7: // Le dernier mois
            this.dateStartPicker = new Date(moment().startOf('month').subtract(1, 'month'))
            this.dateEndPicker = new Date(moment().startOf('month').subtract(1, 'day'))
            break
          case 8: // Le dernier trimestre
            this.dateStartPicker = new Date(moment().subtract(1, 'Q').startOf('Q'))
            this.dateEndPicker = new Date(moment().subtract(1, 'Q').endOf('Q'))
            break
          default:
            this.initPeriod()
        }
      }
      // Formatage et Parsing des dates
      this.dateStartDisplayed = this.formatDate(this.dateStartPicker)
      this.dateStartPicker = this.parseDate(this.dateStartDisplayed)
      this.dateEndDisplayed = this.formatDate(this.dateEndPicker)
      this.dateEndPicker = this.parseDate(this.dateEndDisplayed)
    },
    initAgents () {
      // Sélectionne par défaut tous les agents
      this.agent = this.$store.state._Stats.agents.length
        ? this.$store.state._Stats.agents
        : this.$store.state._User.colleagues
    },
    initBots () {
      // Sélectionne par défaut tous les bots
      this.bot = this.$store.state._Stats.bots.length
        ? this.$store.state._Stats.bots
        : this.$store.state._User.bots
    },
    initMotifs () {
      // Sélectionne par défaut tous les motifs
      this.motif = this.$store.state._Stats.motifs.length
        ? this.$store.state._Stats.motifs
        : []
    },
    initSites () {
      // Sélectionne par défaut tous les sites
      this.site = this.$store.state._Stats.sites.length
        ? this.$store.state._Stats.sites
        : this.$store.state._Sites.sites
    },
    initChannels () {
      this.channel = this.channels
    },

    /**
     * Gestion des fenêtres de dialogues des sites
     */
    openDialogSites () {
      this.dialogSites = true
      this.siteTmp = _.cloneDeep(this.site)
    },
    submitDialogSites () {
      this.dialogSites = false
      this.site = _.cloneDeep(this.siteTmp)
    },

    /**
     * Formate l'affichage de la date
     * @param  {Object} date Moment
     * @return {String}      date à afficher
     */
    formatDate (date) {
      return !date
        ? 'Aucune date'
        : moment(date, 'YYYY[-]MM[-]DD').format('ddd LL')
    },
    /**
     * Parse une date pour la mettre au format ISO
     * @param  {Object} date la date à parser
     * @return {String}      date parsée au format ISO
     */
    parseDate (date) {
      let formatOfDate = moment(date, 'ddd LL').format('ddd LL') === date
        ? 'ddd LL'
        : 'YYYY[-]MM[-]DD'
      return !date || !moment(date, formatOfDate).isValid()
        ? null
        : moment(date, formatOfDate).format('YYYY[-]MM[-]DD')
    }
  },

  watch: {
    // sélection des canaux de communication
    channel (val, oldVal) {
      if (val.length === 1) { // empêche que le select soit vide
        this.channel[0].disabled = true
      } else {
        _.each(this.channel, function (channel) {
          channel.disabled = false
        })
      }
    },
    dateStartPicker (val, oldVal) {
      // update de la date de début à 00:00:00
      this.$store.commit('_Stats/SET_DATE_START', moment(val).hours(0).minutes(0).seconds(0).format())
    },
    dateEndPicker (val, oldVal) {
      // update de la date de fin à 23:59:59
      this.$store.commit('_Stats/SET_DATE_END', moment(val).hours(23).minutes(59).seconds(59).format())
    },
    period (val, oldVal) {
      // update des dates affichées
      this.initDates()
      // update de la période date dans le store
      this.$store.commit('_Stats/SET_PERIOD', val)
    },
    'agent.length': function () {
    },
    agent (v) {
      this.$store.commit('_Stats/SET_AGENTS', v)
    },
    bot (v) {
      this.$store.commit('_Stats/SET_BOTS', v)
    },
    site (v) {
      this.$store.commit('_Stats/SET_SITES', v)
    },
    motif (v) {
      this.$store.commit('_Stats/SET_MOTIFS', v)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Stats.vue